import { z } from 'zod';

import { LocalDateSchema, LocalTimeSchema } from '../admin/attendance';
import { IsoDateTimeSchema } from '../common/datetime';
import { TimezoneSchema } from '../common/fields';
import { AttendanceMarkId, RegularizationRequestId, SiteId } from '../common/ids';

import { AttendanceActionSchema, IdempotencyKeySchema } from './vpp';

/**
 * 007 · regularización de marcas, realm staff (`contracts/staff-api.md`).
 *
 * El trabajador ve sus **huecos** (una marca vigente —el ancla— a la que le falta la pareja) y sus
 * solicitudes. Hacia Staff **nunca** viajan nombres ni ids de administradores, `flags`, `status` de
 * la marca, `location_*`, la clave de idempotencia, `company_id` ni `worker_id` (research R-22): el
 * servidor arma cada DTO campo a campo y, si se equivocara, el serializador descarta lo que sobre.
 *
 * Los tipos compartidos con el Admin (estado, motivo de cancelación, límites de la propuesta, id)
 * viven aquí y `admin/regularizations.ts` los importa.
 */

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

/** Propuestas propias (no originadas por una pedida) por marca ancla (research R-8). */
export const REGULARIZATION_MAX_PROPOSALS = 3;

/** Largo máximo del motivo del trabajador y de la nota del pedido del admin. */
export const REGULARIZATION_REASON_MAX = 300;

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Alias de `RegularizationRequestId` (`common/ids.ts`): `reg_<ULID>`. */
export const RegularizationIdSchema = RegularizationRequestId;

/** Estado de la solicitud (`regularization_status`, data-model.md §2.1). */
export const RegularizationStatusSchema = z.enum([
  'ASKED',
  'PENDING',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
]);
export type RegularizationStatus = z.infer<typeof RegularizationStatusSchema>;

/**
 * Motivo de cancelación (`regularization_cancel_reason`). `WORKER` solo desde `PENDING`, `ADMIN`
 * solo desde `ASKED`; los dos automáticos desde ambos (research R-7).
 */
export const RegularizationCancelReasonSchema = z.enum([
  'WORKER',
  'ADMIN',
  'MARKS_CHANGED',
  'WORKER_DEACTIVATED',
]);
export type RegularizationCancelReason = z.infer<typeof RegularizationCancelReasonSchema>;

/** Estado que ve el trabajador por hueco: `SUGGESTED` = por regularizar, sin solicitud abierta. */
export const StaffRegularizationStateSchema = z.enum([
  'SUGGESTED',
  ...RegularizationStatusSchema.options,
]);
export type StaffRegularizationState = z.infer<typeof StaffRegularizationStateSchema>;

/** Motivo del trabajador: 1–300 caracteres tras `trim`. */
export const RegularizationReasonSchema = z.string().trim().min(1).max(REGULARIZATION_REASON_MAX);
export type RegularizationReason = z.infer<typeof RegularizationReasonSchema>;

// ---------------------------------------------------------------------------
// Entidades
// ---------------------------------------------------------------------------

/** La marca que **sí** existe. Se formatea siempre con su `site_timezone`. */
export const StaffRegularizationAnchorSchema = z.object({
  mark_id: AttendanceMarkId,
  type: AttendanceActionSchema,
  server_timestamp: IsoDateTimeSchema,
  site_timezone: TimezoneSchema,
  site: z.object({ id: SiteId, name: z.string() }),
  operative_day: LocalDateSchema,
});
export type StaffRegularizationAnchor = z.infer<typeof StaffRegularizationAnchorSchema>;

/**
 * Límites de la hora propuesta (research R-3), calculados por el servidor. `time_zone` es la zona
 * **vigente** de la sede del ancla (en la que se escribe). `not_before` y `not_after` son
 * exclusivos frente a las marcas vecinas; `not_after` nunca supera `server_time`.
 */
export const StaffProposalBoundsSchema = z.object({
  time_zone: TimezoneSchema,
  allowed_days: z.array(LocalDateSchema).min(1).max(2),
  not_before: IsoDateTimeSchema,
  not_after: IsoDateTimeSchema,
});
export type StaffProposalBounds = z.infer<typeof StaffProposalBoundsSchema>;

/** La solicitud más reciente del ancla, tal como la ve el trabajador. */
export const StaffRegularizationRequestSchema = z.object({
  id: RegularizationIdSchema,
  status: RegularizationStatusSchema,
  /** La pidió "tu administrador" (sin nombre). */
  asked: z.boolean(),
  ask_note: z.string().nullable(),
  proposed: z
    .object({ date: LocalDateSchema, time: LocalTimeSchema, server_timestamp: IsoDateTimeSchema })
    .nullable(),
  /** El motivo propio. */
  reason: z.string().nullable(),
  requested_at: IsoDateTimeSchema.nullable(),
  resolved_at: IsoDateTimeSchema.nullable(),
  /** Nota al aprobar o motivo del rechazo. */
  resolution_note: z.string().nullable(),
  /** Hora final de la marca creada (puede diferir de la propuesta: "ajustada"). */
  approved_timestamp: IsoDateTimeSchema.nullable(),
  cancel_reason: RegularizationCancelReasonSchema.nullable(),
  created_at: IsoDateTimeSchema,
});
export type StaffRegularizationRequest = z.infer<typeof StaffRegularizationRequestSchema>;

/** Un hueco (o un ancla con solicitud) del trabajador. */
export const StaffRegularizationItemSchema = z.object({
  anchor: StaffRegularizationAnchorSchema,
  missing_type: AttendanceActionSchema,
  state: StaffRegularizationStateSchema,
  /** `SUGGESTED`/`REJECTED`/`CANCELLED` con cupo y plazo, o `ASKED`. */
  can_request: z.boolean(),
  proposals_left: z.number().int().min(0).max(REGULARIZATION_MAX_PROPOSALS),
  /** `null` ⟺ `can_request = false`. */
  bounds: StaffProposalBoundsSchema.nullable(),
  /** La solicitud más reciente del ancla. */
  request: StaffRegularizationRequestSchema.nullable(),
});
export type StaffRegularizationItem = z.infer<typeof StaffRegularizationItemSchema>;

// ---------------------------------------------------------------------------
// Requests / responses
// ---------------------------------------------------------------------------

/** `200` de `GET /attendance/regularizations` (sin query; parámetros desconocidos se descartan). */
export const StaffRegularizationsResponseSchema = z.object({
  items: z.array(StaffRegularizationItemSchema).max(100),
  counts: z.object({
    /** Ítems con `can_request`. */
    actionable: z.number().int().nonnegative(),
    /** Ítems en `PENDING`. */
    pending: z.number().int().nonnegative(),
  }),
  /** `regularization_window_days` de la empresa. */
  window_days: z.number().int().min(1).max(30),
  max_proposals: z.literal(REGULARIZATION_MAX_PROPOSALS),
  /** Día operativo de hoy en la sede principal (como el historial). */
  today: LocalDateSchema,
  server_time: IsoDateTimeSchema,
});
export type StaffRegularizationsResponse = z.infer<typeof StaffRegularizationsResponseSchema>;

/** `POST /attendance/regularizations`: fecha y hora locales de la sede del ancla (zona vigente). */
export const CreateStaffRegularizationBodySchema = z.object({
  anchor_mark_id: AttendanceMarkId,
  date: LocalDateSchema,
  time: LocalTimeSchema,
  reason: RegularizationReasonSchema,
  /** Una por intento de envío; se repite en los reintentos del mismo intento (research R-9). */
  idempotency_key: IdempotencyKeySchema,
});
export type CreateStaffRegularizationBody = z.infer<typeof CreateStaffRegularizationBodySchema>;

/** `201`/`200` de `POST /attendance/regularizations` (`idempotent: true` ⇒ la clave ya la creó). */
export const StaffRegularizationMutationResponseSchema = z.object({
  item: StaffRegularizationItemSchema,
  idempotent: z.boolean(),
});
export type StaffRegularizationMutationResponse = z.infer<
  typeof StaffRegularizationMutationResponseSchema
>;

/** `200` de `POST /attendance/regularizations/{id}/cancel` (body vacío). */
export const StaffRegularizationCancelResponseSchema = z.object({
  item: StaffRegularizationItemSchema,
});
export type StaffRegularizationCancelResponse = z.infer<
  typeof StaffRegularizationCancelResponseSchema
>;

/** Parámetro de ruta `{id}` de solicitud (realms staff y admin). */
export const RegularizationIdParamsSchema = z.object({ id: RegularizationIdSchema });
export type RegularizationIdParams = z.infer<typeof RegularizationIdParamsSchema>;
